/*
 * When Snooze's presets wake a conversation (master-inbox plan, step 3b), on the viewer's own
 * clock: Later today, Tomorrow morning, Next week. Every reading goes through the app's one date
 * module (`src/app-dates.ts`), so a clock change between now and the preset is the zone's, not the
 * browser's guess.
 */
import { resolveWallClock, viewerTimeZone, zoneClock } from "../../src/app-dates.ts";

export type SnoozePreset = {
  readonly id: "later" | "tomorrow" | "week";
  readonly label: string;
  /** The instant, as the API takes it. */
  readonly until: string;
};

const HOUR = 3_600_000;
const MORNING = "08:00";
const EVENING = "18:00";

/** A calendar day `days` after `date` (`2026-10-17`). */
function dayAfter(date: string, days: number): string {
  const [year, month, day] = date.split("-").map(Number);
  return new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, (day ?? 1) + days))
    .toISOString()
    .slice(0, 10);
}

/** The instant a clock reading names in `zone`; null where the clocks skip or repeat it. */
export function wallInstant(local: string, zone: string): number | null {
  const resolved = resolveWallClock(local, zone);
  return resolved.kind === "once" ? resolved.at : null;
}

/** 18:00 while that is an hour or more away; else three hours on, to the hour, if still today. */
function laterToday(now: number, date: string, zone: string): number | null {
  const evening = wallInstant(`${date}T${EVENING}`, zone);
  if (evening !== null && evening - now >= HOUR) return evening;
  const later = Math.ceil((now + 3 * HOUR) / HOUR) * HOUR;
  return zoneClock(zone, later)?.date === date ? later : null;
}

/** Monday morning of next week; a week on when that is tomorrow morning already. */
function nextWeek(date: string, zone: string, tomorrow: number | null): number | null {
  const weekday = new Date(`${date}T00:00:00Z`).getUTCDay();
  const monday = wallInstant(`${dayAfter(date, (8 - weekday) % 7 || 7)}T${MORNING}`, zone);
  if (monday === null || monday !== tomorrow) return monday;
  return wallInstant(`${dayAfter(date, 8)}T${MORNING}`, zone);
}

export function snoozePresets(now: number, zone = viewerTimeZone()): SnoozePreset[] {
  const here = zoneClock(zone, now);
  if (here === null) return [];
  const tomorrow = wallInstant(`${dayAfter(here.date, 1)}T${MORNING}`, zone);
  const found: [SnoozePreset["id"], string, number | null][] = [
    ["later", "Later today", laterToday(now, here.date, zone)],
    ["tomorrow", "Tomorrow morning", tomorrow],
    ["week", "Next week", nextWeek(here.date, zone, tomorrow)],
  ];
  return found.flatMap(([id, label, at]) =>
    at === null ? [] : [{ id, label, until: new Date(at).toISOString() }],
  );
}
